/**
 * Pure helpers for the "Pack files" page: grouping, ordering, search and a
 * small line diff. No library: pack files are short text files.
 */

import type { components } from "@contracts/generated/editor-service";

import type { DirectoryResolution } from "@/api/access";
import { directoryUserFor } from "@/lib/editorAccess";
import { formatRelativeTime } from "@/lib/format";

export type PackFileLayer = components["schemas"]["PackFileLayer"];
type PackRef = components["schemas"]["PackRef"];

export interface PackFileLike {
  path: string;
  edited: boolean;
  /** What the tenant pack does to the file; absent in callers that do not show layers. */
  layer?: PackFileLayer;
}

export interface FileGroup<T extends PackFileLike> {
  /** Directory of the files ("" for the pack root). */
  folder: string;
  /** Lower is shown first. 0 = important clinical folders. */
  rank: number;
  files: T[];
}

/** 1 dynamic evals, 2 classification, 3 summary agent, 4 everything else. */
export function folderRank(path: string): number {
  if (/^conversation_agent\/[^/]+\/dynamic_evals\//.test(path)) return 1;
  if (path.toLowerCase().includes("classif")) return 2;
  if (path.startsWith("summary_agent/")) return 3;
  return 4;
}

export function folderOf(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash < 0 ? "" : path.slice(0, slash);
}

export function fileName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** Which files the tree shows: all, the tenant pack's changes, or this project's own edits. */
export type FileScope = "all" | "tenant" | "mine";

/** A tenant change is an override or an addition; a byte-identical copy of default changes nothing. */
export function inScope(file: PackFileLike, scope: FileScope): boolean {
  if (scope === "mine") return file.edited;
  if (scope === "tenant") {
    return file.layer === "tenant_override" || file.layer === "tenant_added";
  }
  return true;
}

export interface LayerCounts {
  overridden: number;
  added: number;
  identical: number;
  edited: number;
}

export function layerCounts(files: readonly PackFileLike[]): LayerCounts {
  const count = (test: (file: PackFileLike) => boolean) =>
    files.filter(test).length;
  return {
    overridden: count((f) => f.layer === "tenant_override"),
    added: count((f) => f.layer === "tenant_added"),
    identical: count((f) => f.layer === "tenant_same_as_default"),
    edited: count((f) => f.edited),
  };
}

/** The tree badge for a file: null for plain default (and for identical copies, which change nothing). */
export function layerBadge(
  layer: PackFileLayer | undefined,
  tenant: string,
): { label: string; title: string } | null {
  if (layer === "tenant_override") {
    return { label: tenant, title: `${tenant} overrides the default file` };
  }
  if (layer === "tenant_added") {
    return {
      label: `${tenant} +`,
      title: `Added by ${tenant}, not in default`,
    };
  }
  return null;
}

/** One sentence on what the tenant pack does to the open file, for the file header. */
export function layerNote(
  layer: PackFileLayer,
  path: string,
  tenant: string,
): string {
  const merged = path.toLowerCase().endsWith(".yaml");
  switch (layer) {
    case "tenant_override":
      return merged
        ? `${tenant} overrides default here. YAML is merged key by key onto the default file, so ${tenant} only needs the keys it changes.`
        : `${tenant} overrides default here: its file replaces the default file whole.`;
    case "tenant_added":
      return `Added by ${tenant}. Default has no such file.`;
    case "tenant_same_as_default":
      return `${tenant} carries a copy identical to default, so it changes nothing.`;
    case "default":
      return "Inherited from default.";
  }
}

/** "default @2026.09.5 + sheba @2026.09.5 + your edits"; a missing revision is left out, never invented. */
export function packStackLabel(
  base: PackRef | null | undefined,
  tenant: PackRef | null | undefined,
): string {
  const named = (ref: PackRef) =>
    ref.revision ? `${ref.name} @${ref.revision}` : ref.name;
  return [base, tenant]
    .flatMap((ref) => (ref ? [named(ref)] : []))
    .concat("your edits")
    .join(" + ");
}

export type DiffBase = "default" | "tenant" | "previous" | "saved";

/**
 * The texts a file can be compared against; "vs before last save" only when there is a save to undo,
 * "vs saved" only when the editor holds unsaved text.
 */
export function diffOptions(
  file: {
    content: string;
    defaultContent: string | null;
    tenantContent: string | null;
    previousContent?: string | null;
  },
  tenant: string,
  dirty: boolean,
): { id: DiffBase; label: string; text: string }[] {
  return [
    ...(file.defaultContent === null
      ? []
      : [
          {
            id: "default" as const,
            label: "vs default",
            text: file.defaultContent,
          },
        ]),
    ...(file.tenantContent === null
      ? []
      : [
          {
            id: "tenant" as const,
            label: `vs ${tenant}`,
            text: file.tenantContent,
          },
        ]),
    ...(file.previousContent == null
      ? []
      : [
          {
            id: "previous" as const,
            label: "vs before last save",
            text: file.previousContent,
          },
        ]),
    ...(dirty
      ? [{ id: "saved" as const, label: "vs saved", text: file.content }]
      : []),
  ];
}

/** Case-insensitive path substring filter. */
export function filterFiles<T extends PackFileLike>(
  files: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  return needle === ""
    ? [...files]
    : files.filter((file) => file.path.toLowerCase().includes(needle));
}

/** Group by folder; important folders first, then alphabetical. */
export function groupByFolder<T extends PackFileLike>(
  files: readonly T[],
): FileGroup<T>[] {
  const byFolder = new Map<string, T[]>();
  for (const file of files) {
    const folder = folderOf(file.path);
    byFolder.set(folder, [...(byFolder.get(folder) ?? []), file]);
  }
  return [...byFolder.entries()]
    .map(([folder, list]) => ({
      folder,
      rank: Math.min(...list.map((file) => folderRank(file.path))),
      files: [...list].sort((a, b) => a.path.localeCompare(b.path)),
    }))
    .sort((a, b) => a.rank - b.rank || a.folder.localeCompare(b.folder));
}

export type DiffLine = { kind: "same" | "added" | "removed"; text: string };

function splitLines(text: string): string[] {
  return text === "" ? [] : text.split("\n");
}

/** Above this many middle-line pairs the diff falls back to remove-all, add-all. */
const MAX_LCS_CELLS = 4_000_000;

/**
 * Line diff: common prefix and suffix are kept, and the middle is aligned on its
 * longest common subsequence so a moved or distant edit shows as only those lines.
 */
export function diffLines(original: string, current: string): DiffLine[] {
  const a = splitLines(original);
  const b = splitLines(current);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) {
    start += 1;
  }
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const middle: DiffLine[] = [];
  if (midA.length * midB.length > MAX_LCS_CELLS) {
    middle.push(
      ...midA.map((text) => ({ kind: "removed" as const, text })),
      ...midB.map((text) => ({ kind: "added" as const, text })),
    );
  } else {
    // lcs[i][j] = longest common subsequence of midA[i..] and midB[j..].
    const lcs = Array.from({ length: midA.length + 1 }, () =>
      new Array<number>(midB.length + 1).fill(0),
    );
    for (let i = midA.length - 1; i >= 0; i -= 1) {
      for (let j = midB.length - 1; j >= 0; j -= 1) {
        lcs[i]![j] =
          midA[i] === midB[j]
            ? lcs[i + 1]![j + 1]! + 1
            : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
      }
    }
    let i = 0;
    let j = 0;
    while (i < midA.length || j < midB.length) {
      if (i < midA.length && j < midB.length && midA[i] === midB[j]) {
        middle.push({ kind: "same", text: midA[i]! });
        i += 1;
        j += 1;
      } else if (
        i < midA.length &&
        (j === midB.length || lcs[i + 1]![j]! >= lcs[i]![j + 1]!)
      ) {
        middle.push({ kind: "removed", text: midA[i]! });
        i += 1;
      } else {
        middle.push({ kind: "added", text: midB[j]! });
        j += 1;
      }
    }
  }
  return [
    ...a.slice(0, start).map((text) => ({ kind: "same" as const, text })),
    ...middle,
    ...a.slice(endA).map((text) => ({ kind: "same" as const, text })),
  ];
}

/** "Edited by Dana · 3 h ago"; "you" for the signed-in user, "a colleague" when the directory has no name. */
export function editedByNote(
  editedBy: string | null | undefined,
  editedAt: string | null | undefined,
  resolution: DirectoryResolution | undefined,
  selfSubject: string,
  now?: number,
): string | null {
  if (!editedBy) return null;
  const who =
    editedBy === selfSubject
      ? "you"
      : (directoryUserFor(editedBy, resolution)?.displayName ?? "a colleague");
  return `Edited by ${who} · ${formatRelativeTime(editedAt, now)}`;
}
